import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Briefcase, LogOut, RefreshCw, Search, Store, Phone as PhoneIcon, MapPin,
  CheckCircle2, Clock, AlertTriangle, Building2, Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdvisorDashboardPage() {
  const nav = useNavigate();
  const token = localStorage.getItem("holaradar_advisor_token");
  const advisor = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("holaradar_advisor") || "null"); } catch { return null; }
  }, []);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => { if (!token) nav("/asesor/login"); }, [token, nav]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/advisors/me/report`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReport(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("holaradar_advisor_token");
        nav("/asesor/login");
      } else {
        toast.error("No pude cargar tu reporte");
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- carga al montar / al cambiar filtros

  const logout = () => {
    localStorage.removeItem("holaradar_advisor_token");
    localStorage.removeItem("holaradar_advisor");
    nav("/asesor/login");
  };

  const filtered = useMemo(() => {
    if (!report?.merchants) return [];
    const term = q.trim().toLowerCase();
    if (!term) return report.merchants;
    return report.merchants.filter((m) =>
      (m.business_name || "").toLowerCase().includes(term) ||
      (m.phone || "").toLowerCase().includes(term) ||
      (m.address || "").toLowerCase().includes(term)
    );
  }, [q, report]);

  if (!report && loading) return <div className="min-h-screen grid place-items-center bg-black text-white">Cargando…</div>;
  if (!report) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1120] via-neutral-950 to-black text-white">
      <header className="border-b border-white/10 bg-black/50 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#0055FF] to-[#FF007A] grid place-items-center">
              <Briefcase className="w-4 h-4" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold">Asesor Comercial</div>
              <div className="font-heading font-extrabold text-base">{advisor?.full_name || "—"}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={load} variant="ghost" className="text-neutral-400 hover:text-white h-9 rounded-full text-xs" data-testid="advisor-refresh-btn">
              <RefreshCw className="w-3.5 h-3.5" /> Actualizar
            </Button>
            <Button onClick={logout} variant="ghost" className="text-neutral-400 hover:text-white h-9 rounded-full text-xs" data-testid="advisor-logout-btn">
              <LogOut className="w-3.5 h-3.5" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-neutral-500 font-bold">Tu red de comercios</p>
          <h1 className="font-heading font-extrabold text-3xl md:text-4xl">Reporte de comercios</h1>
          <p className="text-sm text-neutral-400 mt-1">
            Aquí ves todos los comercios que registraron tu nombre al inscribirse en HOLA RADAR.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={Building2} label="Total" value={report.stats.total} color="from-[#0055FF] to-[#0044cc]" testid="advisor-stat-total" />
          <StatCard icon={CheckCircle2} label="Al día" value={report.stats.active} color="from-green-500 to-green-700" testid="advisor-stat-active" />
          <StatCard icon={Clock} label="En prueba" value={report.stats.trialing} color="from-amber-500 to-amber-700" testid="advisor-stat-trial" />
          <StatCard icon={AlertTriangle} label="Vencidos" value={report.stats.expired} color="from-red-500 to-red-700" testid="advisor-stat-expired" />
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre, teléfono o dirección…"
            className="pl-9 h-11 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-neutral-500"
            data-testid="advisor-search-input"
          />
        </div>

        {/* Lista */}
        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center">
            <Users className="w-10 h-10 mx-auto text-neutral-500 mb-2" />
            <div className="font-heading font-extrabold text-lg">
              {report.stats.total === 0 ? "Aún no tienes comercios registrados" : "Ningún comercio coincide con tu búsqueda"}
            </div>
            <p className="text-sm text-neutral-400 mt-1 max-w-md mx-auto">
              {report.stats.total === 0
                ? `Cuando un comercio te mencione al registrarse (escribiendo "${advisor?.full_name}" en el campo Asesor Comercial), aparecerá aquí automáticamente.`
                : "Prueba otra búsqueda o limpia el filtro."}
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="advisor-merchants-list">
            {filtered.map((m) => (
              <MerchantRow key={m.merchant_id} m={m} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, testid }) {
  return (
    <div className={`rounded-2xl p-4 bg-gradient-to-br ${color} text-white shadow-lg`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs opacity-90 font-bold">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="font-heading font-extrabold text-4xl mt-1 leading-none">{value}</div>
    </div>
  );
}

function MerchantRow({ m }) {
  const statusMap = {
    active: { label: "Activo", cls: "bg-green-500/20 text-green-300 border-green-500/30" },
    trialing: { label: "En prueba", cls: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
    expired: { label: "Vencido", cls: "bg-red-500/20 text-red-300 border-red-500/30" },
  };
  const s = statusMap[m.subscription_status] || statusMap.expired;
  const vence = m.active_until ? new Date(m.active_until).toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" }) : "—";
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors p-4 grid md:grid-cols-[1fr_auto] gap-3" data-testid={`advisor-merchant-${m.merchant_id}`}>
      <div className="flex gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#FF007A] to-[#0055FF] grid place-items-center shrink-0">
          <Store className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-heading font-extrabold text-base truncate">{m.business_name}</div>
            <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border ${s.cls}`}>
              {s.label}
            </span>
            {m.free_access && <span className="text-[10px] bg-purple-500/20 text-purple-300 border border-purple-500/30 px-2 py-0.5 rounded-full font-bold">PIN gratuito</span>}
          </div>
          <div className="flex items-center gap-3 text-xs text-neutral-400 mt-1 flex-wrap">
            {m.phone && <span className="inline-flex items-center gap-1"><PhoneIcon className="w-3 h-3" /> {m.phone}</span>}
            {m.address && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {m.address}</span>}
            <span className="opacity-60">Categoría: {m.category}</span>
          </div>
        </div>
      </div>
      <div className="text-right">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
          {m.is_trial ? "Prueba termina" : "Vence"}
        </div>
        <div className="font-heading font-extrabold text-sm">{vence}</div>
        <div className="text-[10px] text-neutral-500">
          {m.days_remaining > 0 ? `${m.days_remaining} días restantes` : "Vencido — invítalo a renovar"}
        </div>
      </div>
    </div>
  );
}
